import React from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell, SliderCheckbox } from '@components/common';
import Actions from '@components/common/Actions';
import TextAvatar from '@components/common/TextAvatar';

import { formatDate } from '@utils/kpi-utils/date';
import { generateListOfCategories } from '@utils/tableUtils';

import { ITitle } from '@interfaces/common/common';
import { IChangeStatusOrganisationDTO } from '@interfaces/organisation/dto/organisationsDTO';

import { formatTemplateWithTime, timezoneUTC } from '@constants/common';
import { columnSizes } from '@constants/table';
import { profilePath, removeTooltipText, viewTooltipText } from './constants';

import styles from '@components/common/Table/Table.module.scss';

export const getColumns = ({
	openDeleteConfirm,
	setSingleIdForRemove,
	navigate,
	handleChangeStatusOrganisation,
}: {
	openDeleteConfirm: () => void;
	handleChangeStatusOrganisation: (data: IChangeStatusOrganisationDTO) => void;
	setSingleIdForRemove: React.Dispatch<React.SetStateAction<string | null>>;
	navigate: NavigateFunction;
}) => ([
	{
		header: 'Logo',
		accessorKey: 'logo',
		enableSorting: false,
		size: columnSizes.size70,
		cell: ({ row }: CellContext<any, any>) => {
			const openProfileHandler = () => navigate(`${profilePath}/${row?.original?.id}`);
			const avatarId = row.original?.avatar?.id;

			return (
				<div
					onDoubleClick={openProfileHandler}
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<TextAvatar
						avatarId={avatarId}
						fontSize='10px'
						fontWeight={400}
						fullName={row?.original?.name}
						id={row?.original?.id}
						isProfileLogo
						size='30px'
					/>
				</div>
			);
		},
	},
	{
		header: 'Organisation name',
		accessorKey: 'name',
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={value}
				/>
			);
		},
	},
	{
		header: 'Status',
		accessorKey: 'isActive',
		size: columnSizes.size105,
		cell: ({ row: { original } }: CellContext<any, any>) => {
			const changeStatus = () => {
				handleChangeStatusOrganisation({
					id: original?.id,
					isActive: original?.isActive,
				});
			};

			return (
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-start',
						alignItems: 'center',
						paddingLeft: '15px',
					}}
				>
					<SliderCheckbox
						checked={original?.isActive}
						id={original?.id}
						onChange={changeStatus}
					/>
				</div>
			);
		},
	},
	{
		header: 'organisation type',
		accessorKey: 'types',
		enableSorting: false,
		size: columnSizes.size250,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value: Array<ITitle> = getValue();
			const types = value?.map(role => role.title)?.join(', ');

			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={types}
				/>
			);
		},
	},
	{
		header: 'category',
		accessorKey: 'categories',
		enableSorting: false,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const categories = generateListOfCategories(value);

			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={categories}
				/>
			);
		},
	},
	{
		header: 'Country',
		accessorKey: 'country',
		enableSorting: false,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={value?.name}
				/>
			);
		},
	},
	{
		header: 'City',
		accessorKey: 'city',
		enableSorting: false,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={value}
				/>
			);
		},
	},
	{
		header: 'Created',
		accessorKey: 'createdAt',
		enableSorting: false,
		size: columnSizes.size190,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const date = formatDate(utcToZonedTime(value, timezoneUTC), formatTemplateWithTime);

			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={date}
				/>
			);
		},
	},
	{
		header: () => (
			<div className={styles.centerColumnHeader}>
				Actions
			</div>
		),
		accessorKey: 'id',
		enableSorting: false,
		size: columnSizes.size95,
		cell: ({ getValue }: CellContext<any, any>) => {
			const id = getValue();
			const openProfileHandler = () => navigate(`${profilePath}/${id}`);

			const handleDelete = () => {
				setSingleIdForRemove(id);
				openDeleteConfirm();
			};

			return (
				<Actions
					deleteTooltip={removeTooltipText}
					handleDelete={handleDelete}
					isDelete
					isView
					viewHandler={openProfileHandler}
					viewTooltip={viewTooltipText}
				/>
			);
		},
	},
]);
